import React, { useState, useEffect } from 'react'
import {
  List,
  ListItem,
  ListItemText,
  IconButton,
  Link,
  Divider,
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import styles from './styles.module.css'
import { getAllMessages, deleteMessage } from './TechMessageService'
import { ITechnicianMessage } from '../../../../../app/entities/TechnicianMessage'
import { ILocationPartial } from '../../../../../app/entities/Location'
import { ITechnicianPartial } from '../../../../../app/entities/Technician'

interface TechMessagesListProps {
  onRefresh?: () => void
  refreshTrigger?: number
  locations?: ILocationPartial[]
  allTechnicians?: ITechnicianPartial[]
}

const TechMessagesList: React.FC<TechMessagesListProps> = ({
  onRefresh,
  refreshTrigger,
  locations = [],
  allTechnicians = [],
}) => {
  const [messages, setMessages] = useState<ITechnicianMessage[]>([])
  const [loading, setLoading] = useState<boolean>(true)

  useEffect(() => {
    const loadMessages = async () => {
      setLoading(true)
      try {
        const messageData = await getAllMessages()
        setMessages(messageData)
      } catch (error) {
        console.error('Error fetching messages:', error)
        setMessages([])
      }
      setLoading(false)
    }

    loadMessages()
  }, [refreshTrigger])

  const handleDelete = async (objectId: string) => {
    if (window.confirm('Are you sure you want to delete this message?')) {
      const success = await deleteMessage(objectId)
      if (success) {
        setMessages((prev) =>
          prev.filter((message) => message.objectId !== objectId),
        )
        if (onRefresh) onRefresh()
      }
    }
  }


  const formatRecipients = (targetAudience: string): string => {
    if (!targetAudience) return 'Unknown'

    // All technicians
    if (targetAudience === 'all') {
      return 'All Technicians'
    }

    // Location-based
    if (targetAudience.startsWith('location:')) {
      const locationId = targetAudience.substring(9)
      // Create lookup map for locations
      const locationMap = new Map(locations.map(loc => [loc.objectId, loc]))
      const location = locationMap.get(locationId)
      return location ? `All technicians at ${location.name}` : `Location (${locationId})`
    }

    // Selected technicians
    if (targetAudience.startsWith('selected:')) {
      const techIds = targetAudience.substring(9).split(',')
      // Create lookup map for technicians
      const technicianMap = new Map(allTechnicians.map(tech => [tech.objectId, tech]))
      const techNames = techIds
        .map(id => {
          const technician = technicianMap.get(id)
          return technician?.name || id
        })
        .filter(Boolean)

      if (techNames.length === 0) return 'Selected technicians'
      if (techNames.length === 1) return techNames[0]
      if (techNames.length === 2) return `${techNames[0]} and ${techNames[1]}`
      // Show all names separated by commas
      return techNames.join(', ')
    }

    return targetAudience
  }
  
  const formatDate = (dateValue: string | number | Date) => {
    return new Date(dateValue).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  if (loading) {
    return <div className={styles.loading}>Loading messages...</div>
  }

  if (messages.length === 0) {
    return (
      <div className={styles.formField}>
        <p>No messages have been sent yet.</p>
      </div>
    )
  }

  return (
    <div className={styles.messagesContainer}>
      <h3 className={styles.sectionTitle}>Previous Messages</h3>
      <p className={styles.sectionDescription}>
        These messages have already been sent to technicians.
      </p>

      <List className={styles.messagesList}>
        {messages.map((message, index) => (
          <React.Fragment key={message.objectId}>
            <ListItem
              alignItems='flex-start'
              className={styles.messageItem}
              secondaryAction={
                <IconButton
                  edge='end'
                  aria-label='delete'
                  onClick={() => handleDelete(message.objectId)}
                  className={styles.deleteButton}
                >
                  <DeleteIcon fontSize='small' />
                </IconButton>
              }
            >
              <ListItemText
                primary={
                  <div className={styles.messageHeader}>
                    <span className={styles.messageTitle}>{message.title}</span>
                  </div>
                }
                secondary={
                  <div className={styles.messageContent}>
                    <p className={styles.messageBody}>{message.body}</p>

                    {message.links?.length > 0 && (
                      <div className={styles.linksSection}>
                        <span className={styles.linksSectionTitle}>Links:</span>
                        <ul className={styles.linksList}>
                          {message.links.map((link) => (
                            <li
                              key={link.id}
                              className={styles.linkItem}
                            >
                              <Link
                                href={link.url}
                                target='_blank'
                                rel='noopener noreferrer'
                                className={styles.linkUrl}
                              >
                                {link.text}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <div className={styles.metaContainer}>
                      <p className={styles.metaText}>
                        Created by: {message.createdBy} at{' '}
                        {formatDate(message.created)}
                      </p>
                      <p className={styles.metaText}>
                        {message.scheduleDate && new Date(message.scheduleDate) > new Date()
                          ? 'Scheduled to send at: '
                          : 'Sent at: '}
                        {message.scheduleDate
                          ? formatDate(message.scheduleDate)
                          : formatDate(message.created)}
                      </p>
                      <p className={styles.metaText} style={{
                        maxWidth: '100%',
                        wordWrap: 'break-word'
                      }}>
                        Recipients: {formatRecipients(message.targetAudience)}
                      </p>
                    </div>
                  </div>
                }
              />
            </ListItem>
            {index < messages.length - 1 && <Divider component='li' />}
          </React.Fragment>
        ))}
      </List>
    </div>
  )
}

export default TechMessagesList
